import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  LayoutGrid, Users, Boxes, Activity, AlertTriangle, LogOut,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useI18n } from "../../i18n/LanguageProvider";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StatusPill({ value }) {
  const map = {
    active: "bg-green/10 text-green",
    trial: "bg-black/[0.05] text-black/60",
    inactive: "bg-black/[0.05] text-black/40",
    open: "bg-amber-500/10 text-amber-600",
    resolved: "bg-green/10 text-green",
    high: "bg-red-500/10 text-red-600",
    medium: "bg-amber-500/10 text-amber-600",
    low: "bg-black/[0.05] text-black/50",
  };
  return <span className={`rounded-full px-2.5 py-1 text-xs font-medium capitalize ${map[value] || "bg-black/5"}`}>{value}</span>;
}

function Metric({ label, value }) {
  return (
    <div className="rounded-2xl border border-black/[0.07] bg-white p-6">
      <p className="text-sm text-black/40">{label}</p>
      <p className="mt-2 text-3xl font-semibold tracking-tight text-black">{value}</p>
    </div>
  );
}

export default function Dashboard() {
  const { user, logout, authAxios } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [data, setData] = useState({ metrics: null, users: [], workspaces: [], activity: [], incidents: [] });

  const load = useCallback(async () => {
    const api = authAxios();
    try {
      const [m, u, w, a, inc] = await Promise.all([
        api.get(`${API}/dashboard/metrics`),
        api.get(`${API}/dashboard/users`),
        api.get(`${API}/dashboard/workspaces`),
        api.get(`${API}/dashboard/activity`),
        api.get(`${API}/dashboard/incidents`),
      ]);
      setData({ metrics: m.data, users: u.data, workspaces: w.data, activity: a.data, incidents: inc.data });
    } catch {
      /* handled by auth guard */
    }
  }, [authAxios]);

  useEffect(() => {
    load();
  }, [load]);

  const nav = [
    { id: "overview", label: t("dashboard.nav.overview"), icon: LayoutGrid },
    { id: "users", label: t("dashboard.nav.users"), icon: Users },
    { id: "workspaces", label: t("dashboard.nav.workspaces"), icon: Boxes },
    { id: "activity", label: t("dashboard.nav.activity"), icon: Activity },
    { id: "incidents", label: t("dashboard.nav.incidents"), icon: AlertTriangle },
  ];

  const eur = (n) => `€${(n || 0).toLocaleString("es-ES")}`;
  const m = data.metrics;

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="dashboard-home">
      <aside className="fixed inset-y-0 left-0 hidden w-64 flex-col border-r border-black/5 bg-white p-5 md:flex">
        <img src="/centauros-logo.png" alt="Centauros" style={{ height: 22 }} className="w-auto" />
        <nav className="mt-10 flex-1 space-y-1">
          {nav.map((n) => (
            <button
              key={n.id}
              data-testid={`dash-nav-${n.id}`}
              onClick={() => setTab(n.id)}
              className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${
                tab === n.id ? "bg-black text-white" : "text-black/60 hover:bg-black/[0.04]"
              }`}
            >
              <n.icon strokeWidth={1.5} className="h-4 w-4" />
              {n.label}
            </button>
          ))}
        </nav>
        <div className="border-t border-black/5 pt-4">
          <p className="px-3 text-xs text-black/40">{user?.email}</p>
          <button
            data-testid="dash-logout"
            onClick={async () => { await logout(); navigate("/dashboard/login"); }}
            className="mt-2 flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-black/60 hover:bg-black/[0.04]"
          >
            <LogOut strokeWidth={1.5} className="h-4 w-4" /> {t("dashboard.logout")}
          </button>
        </div>
      </aside>

      <main className="md:pl-64">
        <div className="border-b border-black/5 bg-white/70 backdrop-blur-xl">
          <div className="flex items-center justify-between px-6 py-5 md:px-10">
            <h1 className="text-xl font-semibold tracking-tight text-black">{nav.find((n) => n.id === tab)?.label}</h1>
            <div className="flex gap-1 overflow-x-auto md:hidden">
              {nav.map((n) => (
                <button key={n.id} onClick={() => setTab(n.id)} className={`rounded-full px-3 py-1.5 text-xs ${tab === n.id ? "bg-black text-white" : "text-black/50"}`}>
                  {n.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="p-6 md:p-10">
          {tab === "overview" && m && (
            <div className="space-y-8" data-testid="dash-overview">
              <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
                <Metric label={t("dashboard.metrics.users")} value={m.total_users} />
                <Metric label={t("dashboard.metrics.active")} value={m.active_users} />
                <Metric label={t("dashboard.metrics.workspaces")} value={m.workspaces} />
                <Metric label={t("dashboard.metrics.incidents")} value={m.open_incidents} />
                <Metric label={t("dashboard.metrics.collected")} value={eur(m.total_collected)} />
              </div>
              <div>
                <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-black/40">{t("dashboard.recentActivity")}</h2>
                <div className="space-y-2">
                  {data.activity.map((a) => (
                    <div key={a.activity_id} className="flex items-center gap-3 rounded-xl border border-black/[0.06] bg-white px-4 py-3">
                      <span className="h-1.5 w-1.5 rounded-full bg-green" />
                      <span className="text-sm text-black"><strong className="font-medium">{a.actor}</strong> {a.action}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {tab === "users" && (
            <Table
              testId="dash-users-table"
              cols={[t("dashboard.tableUsers.name"), t("dashboard.tableUsers.email"), t("dashboard.tableUsers.profession"), t("dashboard.tableUsers.status")]}
              rows={data.users.map((u) => [u.name, u.email, u.profession, <StatusPill key={u.user_id} value={u.status} />])}
            />
          )}
          {tab === "workspaces" && (
            <Table
              testId="dash-workspaces-table"
              cols={[t("dashboard.tableWorkspaces.name"), t("dashboard.tableWorkspaces.members"), t("dashboard.tableWorkspaces.plan"), t("dashboard.tableWorkspaces.collected")]}
              rows={data.workspaces.map((w) => [w.name, w.members, w.plan, eur(w.collected)])}
            />
          )}
          {tab === "activity" && (
            <div className="space-y-2" data-testid="dash-activity">
              {data.activity.map((a) => (
                <div key={a.activity_id} className="flex items-center gap-3 rounded-xl border border-black/[0.06] bg-white px-4 py-3">
                  <Activity strokeWidth={1.5} className="h-4 w-4 text-black/30" />
                  <span className="text-sm text-black"><strong className="font-medium">{a.actor}</strong> {a.action}</span>
                </div>
              ))}
            </div>
          )}
          {tab === "incidents" && (
            <Table
              testId="dash-incidents-table"
              cols={[t("dashboard.tableIncidents.title"), t("dashboard.tableIncidents.workspace"), t("dashboard.tableIncidents.severity"), t("dashboard.tableIncidents.status")]}
              rows={data.incidents.map((i) => [i.title, i.workspace, <StatusPill key={`${i.incident_id}s`} value={i.severity} />, <StatusPill key={i.incident_id} value={i.status} />])}
            />
          )}
        </div>
      </main>
    </div>
  );
}

function Table({ cols, rows, testId }) {
  return (
    <div className="overflow-hidden rounded-2xl border border-black/[0.07] bg-white" data-testid={testId}>
      <table className="w-full text-left">
        <thead>
          <tr className="border-b border-black/5">
            {cols.map((c) => (
              <th key={c} className="px-6 py-4 text-xs font-semibold uppercase tracking-wide text-black/40">{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-black/[0.04] last:border-0 hover:bg-black/[0.015]">
              {r.map((cell, j) => (
                <td key={j} className="px-6 py-4 text-sm text-black/80">{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
